import { Checkbox, Flex, Text, TextInput } from "@mantine/core";
import {
  type Control,
  Controller,
  type UseFormRegister,
} from "react-hook-form";
import { useParams } from "react-router-dom";
import Autocomplete from "src/components/shared/_FormInputs/Autocomplete";
import DatePickerInput from "src/components/shared/_FormInputs/DatePickerInput";
import SegmentedControl from "src/components/shared/_FormInputs/SegmentedControl";
import Select from "src/components/shared/_FormInputs/Select";
import { Button } from "src/components/shared/Button/Button";
import { useGetApi } from "src/hooks";
import { type ISearchForm } from "src/pages/SearchPage/SearchPage.types";
import { mapSearchUsers } from "src/pages/SearchPage/SearchPage.utils";
import { getYachtId } from "src/utils";
import { ENTRY_STATUS, entryStatusMap } from "src/utils/entryStatus";

import { SearchFiltersWrapper } from "./Search.styled";
import useNonMaritime from "../../../hooks/useNonMaritime";

interface Props {
  register: UseFormRegister<ISearchForm>;
  control: Control<ISearchForm>;
  onClear: () => void;
  isMedical?: boolean;
}

const SearchFilters = ({ register, control, onClear, isMedical }: Props) => {
  const { replaceMaritimeText } = useNonMaritime();
  const { collectionId } = useParams();
  const { data: users } = useGetApi({
    request: "search-user-list",
    mapper: mapSearchUsers,
    body: {
      params: {
        ...(collectionId ? { lbid: collectionId } : {}),
        yachtid: getYachtId(),
      },
    },
  });

  return (
    <SearchFiltersWrapper>
      <Flex direction="column" gap={16}>
        <Button p={10} onClick={onClear}>
          Clear Filters
        </Button>
        <Controller
          name="ref"
          control={control}
          render={({ field }) => (
            <TextInput
              label="Entry Ref. #"
              {...field}
              value={field.value || ""}
            />
          )}
        />
        <Autocomplete
          control={control}
          name="mention"
          label="Mention"
          data={users ?? []}
        />
        <Autocomplete
          control={control}
          name="opened"
          label="Opened By"
          data={users ?? []}
        />
        <Autocomplete
          control={control}
          name="urn"
          label="URNs"
          data={users ?? []}
        />
        <Select
          label="Status"
          data={[
            { value: "*", label: "All" },
            // Search API expects "0" for Open status, unlike ENTRY_STATUS.DRAFT's label.
            { value: String(ENTRY_STATUS.DRAFT), label: "Open" },
            {
              value: String(ENTRY_STATUS.VERIFIED),
              label: replaceMaritimeText(entryStatusMap[ENTRY_STATUS.VERIFIED].label),
            },
            { value: String(ENTRY_STATUS.WITHDRAWN), label: entryStatusMap[ENTRY_STATUS.WITHDRAWN].label },
          ]}
          name="status"
          control={control}
        />
        <Autocomplete
          control={control}
          name={"explicit"}
          label={isMedical ? "Patient" : "Specific User"}
          data={users ?? []}
        />
        {isMedical && (
          <Autocomplete
            control={control}
            name={"med_condition" as any}
            label="Medical Contition"
            data={[]}
          />
        )}
        <Select
          label="Date Type"
          data={[
            { value: "entrydate", label: "Entry Date" },
            { value: "opendate", label: "Open Date" },
          ]}
          name="dateType"
          control={control}
        />
        <DatePickerInput
          miw={150}
          valueFormat="D MMM YYYY"
          label="Start Date"
          name="startDate"
          control={control}
        />
        <DatePickerInput
          miw={150}
          valueFormat="D MMM YYYY"
          label="End Date"
          name="endDate"
          control={control}
        />
        <div>
          <Text size="sm" fw={500} mt={3}>
            Sort by date
          </Text>
          <SegmentedControl
            data={[
              { label: "Desc", value: "desc" },
              { label: "Asc", value: "asc" },
            ]}
            name="sort"
            control={control}
          />
        </div>
        <Checkbox label="Show Notes" {...register("sub")} />
      </Flex>
    </SearchFiltersWrapper>
  );
};

export default SearchFilters;
